import React, { useId, useState } from 'react';
import { Modal, useClassNameMapper } from 'react-bootstrap';
import SpotlightSearch from '../SpotlightSearch/SpotlightSearch';
import localStyles from './SpotlightSearchOverlay.module.pcss';
import ConfirmationDialog from '@aurora/shared-client/components/common/ConfirmationDialog/ConfirmationDialog';
import useTranslation from '../../useTranslation';
import { EndUserComponent } from '@aurora/shared-types/pages/enums';

interface Props {
  /**
   * showOverlay true to show the current overlay
   */
  showOverlay?: boolean;
  /**
   * setShowOverlay callback function when to show/hide overlay
   */
  setShowOverlay?: (state: boolean) => void;
  /**
   * Initial state of AI chat
   */
  initialAiChatOpen?: boolean;
  /**
   * The initial search prompt, this property is only used to seed the initial state
   * of the query term or AI chat prompt.
   */
  initialPrompt?: string;
}

/**
 * Renders a modal with the spotlight search.
 *
 * @author Sravan Reddy, Willi Hyde
 */
const SpotlightSearchOverlay: React.FC<React.PropsWithChildren<Props>> = ({
  showOverlay,
  setShowOverlay,
  initialAiChatOpen = false,
  initialPrompt = ''
}) => {
  const cx = useClassNameMapper(localStyles);

  const { formatMessage, loading: textLoading } = useTranslation(
    EndUserComponent.SPOTLIGHT_SEARCH_OVERLAY
  );

  const uid = useId();
  const [exiting, setExiting] = useState(false);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [aiChatOpened, setAiChatOpened] = useState(initialAiChatOpen);

  if (textLoading) {
    return null;
  }

  return (
    <>
      <Modal
        show={showOverlay}
        onExiting={() => setExiting(true)}
        onExited={() => setExiting(false)}
        onHide={() => {
          if (!showConfirmationDialog && !aiChatOpened) {
            setShowOverlay(false);
          }
          if (aiChatOpened) {
            setShowConfirmationDialog(true);
          }
        }}
        onEscapeKeyDown={event => {
          if (aiChatOpened) {
            event.preventDefault();
            setShowConfirmationDialog(true);
          }
        }}
        aria-labelledby={uid}
        data-testid="SpotlightSearchOverlay.Modal"
        dialogClassName={cx('lia-modal')}
        contentClassName={cx('lia-search-wrap')}
      >
        <SpotlightSearch
          exiting={exiting}
          onClose={() => {
            setShowOverlay(false);
            setAiChatOpened(false);
          }}
          aiSearchChatOpen={aiChatOpened}
          onAiToggle={state => setAiChatOpened(state)}
          initialPrompt={initialPrompt}
        />
      </Modal>
      <ConfirmationDialog
        className={cx('lia-confirmation-dialog')}
        backdrop={false}
        show={showConfirmationDialog}
        onEscapeKeyDown={event => {
          setShowConfirmationDialog(false);
          event.preventDefault();
        }}
        onHide={(): void => {
          setShowOverlay(false);
        }}
        onSubmit={(): void => {
          setShowOverlay(false);
          setAiChatOpened(false);
          setShowConfirmationDialog(false);
        }}
        onCancel={(): void => setShowConfirmationDialog(false)}
        titleText={formatMessage('confirmationTitle')}
        bodyText={formatMessage('confirmationBody')}
        submitButtonText={formatMessage('confirmationSubmit')}
      />
    </>
  );
};

export default SpotlightSearchOverlay;
